import { IterableMap } from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import LeftArrowIcon from "@redotech/redo-icons/arrow-left.svg";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { SettingsPermissions } from "@redotech/redo-model/permissions";
import {
  ChoiceState,
  MultipleChoiceState,
} from "@redotech/redo-return-ui/flows/return-flow/multiple-choice";
import {
  StepId,
  StepTypeDetailsProps,
} from "@redotech/redo-return-ui/flows/step";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCollapseSubsection } from "@redotech/redo-web/arbiter-components/collapse/redo-collapse-section";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoTextareaInputField } from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import {
  ButtonSize,
  IconButton,
  IconButtonTheme,
} from "@redotech/redo-web/button";
import { Flex } from "@redotech/redo-web/flex";
import Info from "@redotech/redo-web/icon-old/info.svg";
import { Text } from "@redotech/redo-web/text";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { produce } from "immer";
import { memo, ReactNode, useContext } from "react";
import * as multipleChoiceCss from "./multiple-choice.module.css";
import { RequiredMessage } from "./required-message";

interface AdditionalAiInfoProps {
  canEditReturnsSettings: boolean;
  setAdditionalAiInfo: (additionalAiInfo: string | undefined) => void;
  currentAdditionalAiInfo: string | undefined;
  collapsedTitle?: string;
  tooltip?: ReactNode;
}

export const AdditionalAiInfo = memo(function AdditionalAiInfo({
  canEditReturnsSettings,
  setAdditionalAiInfo,
  currentAdditionalAiInfo,
  collapsedTitle = "Advanced options",
}: AdditionalAiInfoProps) {
  return (
    <RedoCollapseSubsection title={collapsedTitle}>
      <Flex dir="column">
        <Flex align="center">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Additional context for variant exchange AI
          </Text>
          <Tooltip
            enterTouchDelay={0}
            placement="left"
            title="Provide additional information to help AI make better exchange suggestions"
          >
            <IconButton>
              <Info className={multipleChoiceCss.infoTooltipIcon} />
            </IconButton>
          </Tooltip>
        </Flex>
        <RedoTextareaInputField
          readOnly={!canEditReturnsSettings}
          setValue={setAdditionalAiInfo}
          style={{ minWidth: "unset" }}
          value={currentAdditionalAiInfo || ""}
        />
      </Flex>
    </RedoCollapseSubsection>
  );
});

export const MultipleChoiceDetails = memo(function MultipleChoiceDetails({
  stepSelect,
  state,
  setState,
}: StepTypeDetailsProps<MultipleChoiceState>) {
  const user = useContext(UserContext);
  const canEditReturnsSettings = useHasPermission(
    SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
  );
  const handleAdd = useHandler(() =>
    setState((state) => ({
      ...state,
      choices: [...state.choices, { name: "", next: undefined, code: "" }],
    })),
  );
  const handlePrompt = useHandler((prompt: string) =>
    setState((state) => ({ ...state, prompt })),
  );
  const handleDescription = useHandler((description: string) => {
    setState((state) => ({ ...state, description }));
  });
  const handleShuffle = useHandler((shuffle: boolean) =>
    setState((state) => ({ ...state, shuffle })),
  );
  const handleFocus = useHandler((focused: boolean) => {
    if (!focused) {
      setState((state) => ({ ...state, attempted: true }));
    }
  });
  const handleAdditionalAiInfo = useHandler(
    (additionalAiInfo: string | undefined) =>
      setState((state) => ({ ...state, additionalAiInfo })),
  );
  const isRedoUser =
    (user?.email && /@getredo\.com/.test(user?.email)) || false;

  return (
    <>
      <RedoToggle
        description="This will shuffle the order that return reasons are displayed for each individual order"
        disabled={!canEditReturnsSettings}
        label="Shuffle options"
        setValue={handleShuffle}
        value={state.shuffle ?? false}
      />
      <Text fontSize="xs" textColor="secondary">
        Prompt
      </Text>
      <RedoTextareaInputField
        errorText={state.attempted && !state.prompt ? "Required" : undefined}
        readOnly={!canEditReturnsSettings}
        setValue={handlePrompt}
        value={state.prompt}
      />
      <Text fontSize="xs" textColor="secondary">
        Description
      </Text>
      <RedoTextareaInputField
        readOnly={!canEditReturnsSettings}
        setValue={handleDescription}
        value={state.description}
      />
      <Text fontSize="xxs" textColor="tertiary">
        Additional help or explanation. Can include HTML.
      </Text>
      {isRedoUser ? (
        <AdditionalAiInfo
          canEditReturnsSettings={canEditReturnsSettings}
          currentAdditionalAiInfo={state.additionalAiInfo}
          setAdditionalAiInfo={handleAdditionalAiInfo}
        />
      ) : null}
      <Flex dir="column" gap="md">
        <IterableMap items={state.choices} keyFn={(_, index) => index}>
          {(choice, index) => (
            <StateChoiceComponent
              canEditReturnsSettings={canEditReturnsSettings}
              choice={choice}
              handleFocus={handleFocus}
              index={index}
              isRedoUser={isRedoUser}
              reason={state.reason}
              setState={setState}
              state={state}
              stepSelect={stepSelect}
            />
          )}
        </IterableMap>
      </Flex>
      <Flex align="flex-start" pt="md">
        <RedoButton
          disabled={!canEditReturnsSettings}
          hierarchy="secondary"
          IconLeading={PlusIcon}
          onClick={handleAdd}
          size="sm"
          text="Add option"
        />
      </Flex>
      {!state.choices.length && (
        <RequiredMessage>Must have at least one option</RequiredMessage>
      )}
    </>
  );
});

const StateChoiceComponent = memo(function StateChoiceComponent({
  state,
  setState,
  stepSelect,
  handleFocus,
  canEditReturnsSettings,
  choice,
  index,
  reason,
  isRedoUser,
}: {
  state: StepTypeDetailsProps<MultipleChoiceState>["state"];
  setState: StepTypeDetailsProps<MultipleChoiceState>["setState"];
  stepSelect: StepTypeDetailsProps<MultipleChoiceState>["stepSelect"];
  handleFocus(focused: boolean): void;
  canEditReturnsSettings: boolean;
  choice: ChoiceState;
  index: number;
  reason: boolean;
  isRedoUser: boolean;
}) {
  const setName = useHandler((name: string) =>
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.choices[index].name = name;
      }),
    ),
  );
  const setAdditionalAiInfo = useHandler(
    (additionalAiInfo: string | undefined) =>
      setState((state) =>
        produce(state, (state) => {
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          state.choices[index].additionalAiInfo = additionalAiInfo;
        }),
      ),
  );
  const setCode = useHandler((code: string | undefined) =>
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.choices[index].code = code;
      }),
    ),
  );
  const setStep = useHandler((next: StepId | undefined) =>
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.choices[index].next = next;
      }),
    ),
  );
  const handleUp = useHandler(() => {
    setState((state) =>
      produce(state, (state) => {
        const choices = state.choices.splice(index, 1);
        state.choices.splice(index - 1, 0, ...choices);
      }),
    );
  });
  const handleDown = useHandler(() => {
    setState((state) =>
      produce(state, (state) => {
        const choices = state.choices.splice(index, 1);
        state.choices.splice(index + 1, 0, ...choices);
      }),
    );
  });
  const handleDelete = useHandler(() => {
    setState((state) =>
      produce(state, (state) => {
        state.choices.splice(index, 1);
      }),
    );
  });
  return (
    <Flex className={multipleChoiceCss.optionCard} dir="column">
      <Flex align="center" gap="xs" justify="space-between">
        <Text fontSize="sm" fontWeight="semibold" textColor="primary">
          Option #{index + 1}
        </Text>
        {canEditReturnsSettings && (
          <Flex>
            <IconButton
              disabled={!index}
              onClick={handleUp}
              size={ButtonSize.SMALL}
            >
              <LeftArrowIcon className={multipleChoiceCss.upArrow} />
            </IconButton>
            <IconButton
              disabled={index === state.choices.length - 1}
              onClick={handleDown}
              size={ButtonSize.SMALL}
            >
              <LeftArrowIcon className={multipleChoiceCss.downArrow} />
            </IconButton>
            <IconButton
              onClick={handleDelete}
              size={ButtonSize.SMALL}
              theme={IconButtonTheme.DANGER}
            >
              <TrashIcon />
            </IconButton>
          </Flex>
        )}
      </Flex>
      <RedoTextInput
        errorText={state.attempted && !choice.name ? "Required" : undefined}
        label="Return reason"
        onBlur={(event) => {
          if (!choice.name) {
            handleFocus(false);
          }
        }}
        setValue={setName}
        state={canEditReturnsSettings ? "default" : "readonly"}
        value={choice.name}
      />
      {reason && (
        <RedoTextInput
          label="Reason code"
          onBlur={(event) => {
            if (!choice.code) {
              handleFocus(false);
            }
          }}
          setValue={setCode}
          state={canEditReturnsSettings ? "default" : "readonly"}
          trailingActions={
            <Tooltip
              enterTouchDelay={0}
              placement="top"
              title="Redo will provide reason codes in api responses and webhook payloads"
            >
              <IconButton>
                <Info className={multipleChoiceCss.infoTooltipIcon} />
              </IconButton>
            </Tooltip>
          }
          value={choice.code || ""}
        />
      )}

      <Text fontSize="xs" textColor="secondary">
        Next step #{index + 1}
      </Text>
      {state.attempted && !choice.next && (
        <Text fontSize="xs" fontWeight="regular" textColor="error">
          Required
        </Text>
      )}
      {stepSelect({
        value: choice.next,
        handleIndex: index,
        valueChange: setStep,
      })}
      {isRedoUser ? (
        <AdditionalAiInfo
          canEditReturnsSettings={canEditReturnsSettings}
          currentAdditionalAiInfo={choice.additionalAiInfo}
          setAdditionalAiInfo={setAdditionalAiInfo}
        />
      ) : null}
    </Flex>
  );
});
